<template>
    <modal v-model="showUserDetails" :force-close="true">
        <div class="user-details">
            <div class="user-header darkmap">
                <avatar-icon class="icon" :user="user" />
                <div class="user-info">
                    <div class="user-name">
                        {{ user?.displayName }}
                    </div>
                    <div class="user-email">
                        {{ user?.email }}
                    </div>
                    <div class="user-created">
                        {{ $t('user_details.account_created') }} {{ userCreation }} <br />
                        {{ $t('user_details.last_sign_in') }} {{ userLastSignIn }}
                    </div>
                </div>
                <div class="user-close" @click="emit('close')">
                    <close-icon />
                </div>
            </div>

            <div class="user-content">
                <!-- <list-menu> -->

                <list-row>
                    {{ $t('user_details.waypoints') }}
                    <template #icon>
                        <waypoint-icon />
                    </template>
                    <template #data>
                        <badge> {{ userSummary.waypoints }} </badge>
                    </template>
                </list-row>

                <list-row>
                    {{ $t('user_details.tracks') }}
                    <template #icon>
                        <track-icon />
                    </template>
                    <template #data>
                        <badge> {{ userSummary.tracks }} </badge>
                    </template>
                </list-row>

                <list-row>
                    {{ $t('user_details.drawings') }}
                    <template #icon>
                        <drawing-icon />
                    </template>
                    <template #data>
                        <badge> {{ userSummary.drawings }} </badge>
                    </template>
                </list-row>

                <list-row>
                    {{ $t('user_details.contacts') }}
                    <template #icon>
                        <avatar-icon :user="null" />
                    </template>
                    <template #data>
                        <badge> {{ userSummary.contacts }} </badge>
                    </template>
                </list-row>

                <list-row>
                    {{ $t('user_details.map_layers') }}
                    <template #icon>
                        <resource-layers-icon :state="true" />
                    </template>
                    <template #data>
                        <badge> {{ userSummary.layers }} </badge>
                    </template>
                </list-row>

                <list-row>
                    {{ $t('user_details.landowner_maps') }}
                    <template #icon>
                        <landowner-layers-icon :state="true" />
                    </template>
                    <template #data>
                        <badge> {{ purchasedMaps.length }} </badge>
                    </template>
                </list-row>
                <!-- </list-menu> -->
                <div class="subscriptions">
                    <list-header>
                        <template #title>
                            {{ $t('user_details.subscriptions') }}
                        </template>
                        <template #data>
                            {{ purchasedSubscriptions.length }}
                        </template>
                    </list-header>
                    <list-row v-for="subscription in purchasedSubscriptions" :key="subscription.sku">
                        <region-subscription-tier-label
                            :subscription-tier="subscription.tier"
                            :region="subscription.region"
                            :include-product="true"
                        ></region-subscription-tier-label>
                        <template #icon>
                            <region-icon :state="true" :region="subscription.region" />
                        </template>
                    </list-row>
                </div>
            </div>
        </div>
    </modal>
</template>

<script setup>
import { onMounted, computed, watch, ref } from 'vue';
import { useI18n as usePathfinderI18n } from 'ihunter-pathfinder-components';

import Modal from '../notify/Modal.vue';
import AvatarIcon from '../icons/AvatarIcon.vue';
import CloseIcon from '../icons/navstack/CloseIcon.vue';
import ListRow from '../lists/ListRow.vue';
import ListHeader from '../lists/ListHeader.vue';
import Badge from '../input/Badge.vue';
import WaypointIcon from '../icons/collection/WaypointIcon.vue';
import TrackIcon from '../icons/collection/TrackIcon.vue';
import DrawingIcon from '../icons/collection/DrawingIcon.vue';
import ResourceLayersIcon from '../icons/maps/ResourceLayersIcon.vue';
import LandownerLayersIcon from '../icons/maps/LandownerLayersIcon.vue';
import RegionIcon from '../icons/RegionIcon.vue';
import RegionSubscriptionTierLabel from '../brand/RegionSubscriptionTierLabel.vue';

import { useUserSummary } from '../composables/useUserSummary';
import { useAuthentication } from '../composables/useAuthentication';
import { usePurchases } from '../composables/usePurchases';
import { useAnalytics } from '../composables/useAnalytics';

const { purchasedMaps, purchasedSubscriptions } = usePurchases();
const { userSummary, populateUserSummary } = useUserSummary();
const { user } = useAuthentication();
const { logDialogOpened, logDialogClosed } = useAnalytics();

const showUserDetails = defineModel({
    type: Boolean,
    default: false,
});

const { getShortDateFromTimestamp } = usePathfinderI18n();

const userCreation = computed(() => getShortDateFromTimestamp(user.value?.metadata?.createdAt));

const userLastSignIn = computed(() => getShortDateFromTimestamp(user.value?.metadata?.lastLoginAt));

const emit = defineEmits(['close']);

onMounted(async () => {
    if (user.value) {
        await initializeUserDetails();
    }
});

watch(user, async (user) => {
    if (user) {
        await initializeUserDetails();
    }
});

watch(showUserDetails, (isOpen) => {
    if (isOpen) {
        logDialogOpened('user_details');
    } else {
        logDialogClosed();
    }
});

const initializeUserDetails = async () => {
    try {
        if (user.value.isTestUser) {
            return;
        }
        await populateUserSummary(user.value);
        console.log(`initializeUserDetails`, userSummary.value);
    } catch (error) {
        console.error('initializeUserDetails', error);
    }
};
</script>

<style scoped lang="scss">
@use 'ihunter-pathfinder/styles' as Pathfinder;

.user-details {
    display: flex;
    flex-direction: column;
    background: var(--surface);
    width: 100%;
    min-width: 250px; // Needed?
    max-width: 396px;
    user-select: none;
    border-radius: Pathfinder.$radius-100;
    overflow: hidden;

    .user-header {
        display: grid;
        width: 100%;
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto auto;
        column-gap: Pathfinder.$space-200;
        align-items: start;
        background: var(--surface);
        color: var(--on-surface);
        color: white;
        padding: Pathfinder.$space-200;

        .icon {
            grid-column: 1;
            grid-row: 1;
            height: Pathfinder.$avatar-default;
            width: Pathfinder.$avatar-default;
        }

        .user-info {
            @include Pathfinder.Macro-Text;
            grid-column: 2;
            grid-row: 1;
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            justify-content: start;
            gap: Pathfinder.$space-25;

            .user-email {
                @include Pathfinder.Micro-Text;
                margin-bottom: Pathfinder.$space-100;
                color: var(--selection);
            }

            .user-created {
                @include Pathfinder.Micro-Text;
            }
        }

        .user-close {
            grid-column: 3;
            grid-row: 1;
            height: Pathfinder.$icon-small;
            width: Pathfinder.$icon-small;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;

            // ::v-deep(svg) {
            fill: var(--on-surface);
            // }
        }
    }

    .user-content {
        display: flex;
        flex-direction: column;
        width: 100%;

        min-height: 0;
        min-width: 0;
        overflow: auto; // In case a child overflows horizontally in Safari
    }

    // @include Pathfinder.below(min) {

    // }
}
</style>
